/**
 * usePackerScanning — Barcode scanning logic for the packer flow.
 * Handles: weight-entry station/badge scans, confirm-step badge/label scans,
 * and manual barcode entry submission.
 */
import { useCallback } from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { useBarcodeScanner } from '../../../hooks/useBarcodeScanner';
import operatorService from '../../../services/operatorService';
import { isBadgeCode, normalizeBadgeCode, extractEmployeeNumber } from '../../../utils/barcodeUtils';
import toast from 'react-hot-toast';
import type { EmployeeInfo, SessionInfo } from '../../../types';

interface StationInfo {
  station_id: number;
  station_name: string;
  station_barcode?: string;
  location_code?: string;
  printer_mac_id?: string;
}

interface UsePackerScanningParams {
  session: SessionInfo;
  employee: EmployeeInfo;
  station: StationInfo;
  step: 'weight_entry' | 'printing' | 'scan_confirm' | 'success';
  scanStep: 'badge' | 'label';
  scannedBadge: string | null;
  stationScannedForMenu: boolean;
  setStationScannedForMenu: (v: boolean) => void;
  showBadgeMenu: boolean;
  setShowBadgeMenu: (v: boolean) => void;
  isClockingOut: boolean;
  showSupervisorOverride: boolean;
  badgeAcceptedAtRef: React.MutableRefObject<number>;
  manualInput: string;
  setManualInput: (v: string) => void;
  setStep: (step: 'weight_entry' | 'printing' | 'scan_confirm' | 'success') => void;
  setScanStep: (s: 'badge' | 'label') => void;
  setScannedBadge: (b: string | null) => void;
  setWeight: (w: string) => void;
  setBoxCount: React.Dispatch<React.SetStateAction<number>>;
  setSubUnitsPacked: (n: number) => void;
  setSubUnitIds: React.Dispatch<React.SetStateAction<number[]>>;
  setShowTargetReachedModal: (v: boolean) => void;
  setIsLoading: (loading: boolean) => void;
}

export function usePackerScanning({
  session,
  employee,
  station,
  step,
  scanStep,
  scannedBadge,
  stationScannedForMenu,
  setStationScannedForMenu,
  showBadgeMenu,
  setShowBadgeMenu,
  isClockingOut,
  showSupervisorOverride,
  badgeAcceptedAtRef,
  manualInput,
  setManualInput,
  setStep,
  setScanStep,
  setScannedBadge,
  setWeight,
  setBoxCount,
  setSubUnitsPacked,
  setSubUnitIds,
  setShowTargetReachedModal,
  setIsLoading,
}: UsePackerScanningParams) {
  const { t } = useLanguage();

  // Handle scan when on weight entry - station first, then badge to show options menu
  const handleWeightEntryScan = useCallback((barcode: string) => {
    if (step !== 'weight_entry') return;

    // Check if it's a station barcode scan
    if (barcode.startsWith('#STN-') || barcode.startsWith('#')) {
      // Verify it's the correct station
      const stationBarcode = station?.station_barcode || `#STN-${station?.station_id}`;
      if (barcode === stationBarcode || barcode === `#STN-${station?.station_id}`) {
        setStationScannedForMenu(true);
        toast.success(t('mobile.stationScanned') || 'Station scanned - now scan your badge');
      } else {
        toast.error(t('mobile.wrongStation') || 'Wrong station');
      }
      return;
    }

    // Check if it's a badge scan (handles @+, @i, @ prefixes)
    if (isBadgeCode(barcode) || (!barcode.startsWith('#') && !barcode.includes('-'))) {
      if (!stationScannedForMenu) {
        toast.error(t('mobile.scanStationFirst') || 'Please scan station first');
        return;
      }
      const scannedNumber = extractEmployeeNumber(barcode);
      if (scannedNumber === employee.employee_number) {
        setStationScannedForMenu(false); // Reset for next time
        setShowBadgeMenu(true);
      } else {
        toast.error(t('mobile.differentBadge') || 'Different badge scanned');
      }
    }
  }, [step, employee.employee_number, stationScannedForMenu, station, t]);

  // Listen for badge scans on weight entry screen
  useBarcodeScanner({
    onScan: handleWeightEntryScan,
    enabled: step === 'weight_entry' && !showBadgeMenu && !isClockingOut && !showSupervisorOverride,
    minLength: 4,
  });

  // Handle barcode scan for confirmation
  const handleConfirmScan = useCallback(async (barcode: string) => {
    if (step !== 'scan_confirm') return;

    if (scanStep === 'badge') {
      // Expecting badge scan (handles @+, @i, @ prefixes)
      if (isBadgeCode(barcode) || !barcode.startsWith('#')) {
        const code = normalizeBadgeCode(barcode);
        const scannedEmployeeNumber = extractEmployeeNumber(barcode);

        // Only allow the clocked-in packer to confirm
        if (scannedEmployeeNumber !== employee.employee_number) {
          toast.error(t('mobile.wrongPackerBadge') || 'Wrong badge - only the clocked-in packer can confirm');
          return;
        }

        setScannedBadge(code);
        badgeAcceptedAtRef.current = Date.now();
        setManualInput('');
        setScanStep('label');
        toast.success(t('mobile.badgeScanned') || 'Badge scanned');
      } else {
        toast.error(t('mobile.scanBadgeFirst') || 'Please scan your badge first');
      }
    } else if (scanStep === 'label') {
      // Ignore scans within 500ms of badge acceptance (scanner echo/double-fire)
      if (Date.now() - badgeAcceptedAtRef.current < 500) return;

      // Expecting label scan - reject if it's a badge
      if (isBadgeCode(barcode)) {
        toast.error(t('mobile.scanLabelNotBadge') || 'Please scan the box label, not badge');
        return;
      }

      // Verify with backend
      setIsLoading(true);
      try {
        const result = await operatorService.confirmBoxScan({
          badge_code: scannedBadge!,
          box_label: barcode,
          sub_session_number: session!.sub_session_number,
        });

        if (result.success) {
          setStep('success');
          // Sync box count from backend to prevent drift
          const backendBoxCount = result.progress?.current_boxes;
          if (backendBoxCount !== undefined) {
            // Ensure it's a number (API might return string)
            const parsedCount = typeof backendBoxCount === 'string' ? parseInt(backendBoxCount, 10) : backendBoxCount;
            setBoxCount(parsedCount);
          } else {
            setBoxCount(prev => prev + 1);
          }
          toast.success(t('mobile.boxConfirmed') || 'Box confirmed!');

          // Check if target box count has been reached
          if (result.progress?.target_reached) {
            // Show target reached modal after brief success display
            setTimeout(() => {
              setStep('weight_entry');
              setWeight('');
              setScanStep('badge');
              setScannedBadge(null);
              setSubUnitsPacked(0);
              setSubUnitIds([]);
              setShowTargetReachedModal(true);
            }, 1000);
          } else {
            // Reset for next box after delay
            setTimeout(() => {
              setStep('weight_entry');
              setWeight('');
              setScanStep('badge');
              setScannedBadge(null);
              setSubUnitsPacked(0);
              setSubUnitIds([]);
            }, 1500);
          }
        } else {
          toast.error(result.message || 'Box confirmation failed');
          setScanStep('label'); // Stay on label scan
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Confirmation failed');
      } finally {
        setIsLoading(false);
      }
    }
  }, [step, scanStep, scannedBadge, session, t]);

  // Barcode scanner hook
  useBarcodeScanner({
    onScan: handleConfirmScan,
    enabled: step === 'scan_confirm' && !showSupervisorOverride,
    minLength: 4,
  });

  // Handle manual submit for scan confirmation
  const handleManualScanSubmit = () => {
    if (manualInput.trim()) {
      handleConfirmScan(manualInput.trim());
      setManualInput('');
    }
  };

  return { handleManualScanSubmit, handleConfirmScan, handleWeightEntryScan };
}
